import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';

import { useToast } from '../../../common/hooks/useToast';
import { CrmPageFrame } from '../CrmPageFrame';
import { newWorkflowPath, workflowPath } from '../crmPaths';
import { count } from '../manage/manage';
import { DeleteDialog, EmptyState, LoadingRows, StatTile, StatTiles, TABLE_SX, visuallyHidden } from '../manage/ManageParts';
import { useDebounced } from '../tagging/useDebounced';
import { describeTrigger, describeWorkflowError, isMigrated, STATUS_META, Workflow } from './workflowModel';
import { CRM_DELETE_WORKFLOW, CRM_DUPLICATE_WORKFLOW, CRM_WORKFLOW_TOTALS, CRM_WORKFLOWS } from './workflows.queries';

type View = 'running' | 'drafts' | 'paused' | 'ended' | 'migrated' | 'all';

const VIEWS: Record<View, { status: Array<string> | null; source: string | null }> = {
  all: { status: null, source: 'MANUAL' },
  running: { status: ['RUNNING'], source: 'MANUAL' },
  drafts: { status: ['DRAFT'], source: 'MANUAL' },
  paused: { status: ['PAUSED'], source: 'MANUAL' },
  ended: { status: ['ENDED', 'ARCHIVED'], source: 'MANUAL' },
  migrated: { status: null, source: 'NATIONBUILDER' },
};

const TILES: Array<{ view: Exclude<View, 'all'>; label: string; hint: string; icon: React.ReactNode; tone: string }> = [
  { view: 'running', label: 'Running', hint: 'Enrolling and sending now', icon: <PlayCircleOutlineIcon />, tone: '#059669' },
  { view: 'drafts', label: 'Drafts', hint: 'Not started yet', icon: <EditNoteOutlinedIcon />, tone: '#4f46e5' },
  { view: 'paused', label: 'Paused', hint: 'Steps wait until resumed', icon: <PauseCircleOutlineIcon />, tone: '#d97706' },
  { view: 'ended', label: 'Ended', hint: 'Finished or archived', icon: <StopCircleOutlinedIcon />, tone: '#64748b' },
  { view: 'migrated', label: 'From NationBuilder', hint: 'History; copy one to reuse it', icon: <HistoryOutlinedIcon />, tone: '#7c3aed' },
];

const PAGE = 25;

const parseView = (value: string | null): View => (value && value in VIEWS ? (value as View) : 'all');

/** Workflows: automated sequences of emails, texts, tags and call tasks. Plan 4.5. */
export const CrmWorkflows = () => {
  const [params, setParams] = useSearchParams();
  const view = parseView(params.get('show'));
  const search = params.get('q') ?? '';
  const page = Math.max(0, Number(params.get('page')) || 0);
  const go = (next: { view?: View; search?: string; page?: number }) => {
    const p = new URLSearchParams();
    const v = next.view ?? view;
    const s = next.search ?? search;
    if (v !== 'all') p.set('show', v);
    if (s.trim()) p.set('q', s.trim());
    if (next.page) p.set('page', String(next.page));
    setParams(p, { replace: true });
  };

  const [typed, setTyped] = useState(search);
  const settled = useDebounced(typed.slice(0, 200));
  useEffect(() => {
    if (settled.trim() !== search.trim()) go({ search: settled, page: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled]);

  const navigate = useNavigate();
  const client = useApolloClient();
  const toast = useToast();
  const [deleting, setDeleting] = useState<Workflow | null>(null);

  const { data, previousData, loading, error, refetch } = useQuery<{ crmWorkflows: { total: number; items: Array<Workflow> } }>(CRM_WORKFLOWS, {
    variables: { ...VIEWS[view], search: search.trim() || null, skip: page * PAGE, take: PAGE },
    notifyOnNetworkStatusChange: true,
  });
  const totals = useQuery<Record<string, { total: number }>>(CRM_WORKFLOW_TOTALS);
  const [duplicate] = useMutation(CRM_DUPLICATE_WORKFLOW);
  const [remove, removal] = useMutation(CRM_DELETE_WORKFLOW);

  const shown = (data ?? previousData)?.crmWorkflows;
  const forget = () => {
    client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'crmWorkflows' });
    client.cache.gc();
  };

  const copy = async (w: Workflow) => {
    try {
      const { data: made } = await duplicate({ variables: { id: w.id } });
      forget();
      toast.success(`Made ${made.crmDuplicateWorkflow.name}, a draft.`);
      navigate(workflowPath(made.crmDuplicateWorkflow.id));
    } catch (e) {
      toast.error(describeWorkflowError(e, 'Could not copy it.'));
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await remove({ variables: { id: deleting.id } });
      forget();
      toast.success(`Deleted ${deleting.name}.`);
      setDeleting(null);
    } catch {
      // Shown in the dialog.
    }
  };

  const newButton = (
    <Button component={RouterLink} to={newWorkflowPath} variant="contained" startIcon={<AddIcon />}>
      New workflow
    </Button>
  );

  return (
    <CrmPageFrame
      title="Workflows"
      description="Automated sequences: when something happens, send, tag or create a call task, wait, then the next step."
      actions={newButton}
    >
      <Box sx={{ display: 'grid', gap: 2 }}>
        <StatTiles label="Show" columns={5}>
          {TILES.map((t) => (
            <StatTile
              key={t.view}
              label={t.label}
              hint={t.hint}
              icon={t.icon}
              tone={t.tone}
              value={totals.data?.[t.view]?.total}
              selected={view === t.view}
              onClick={() => go({ view: view === t.view ? 'all' : t.view, page: 0 })}
            />
          ))}
        </StatTiles>

        <Box sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', overflow: 'hidden' }}>
          <Box sx={{ p: { xs: 1.5, sm: 2 }, display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              placeholder="Search by name"
              fullWidth
              inputProps={{ 'aria-label': 'Search workflows', maxLength: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: typed ? (
                  <InputAdornment position="end">
                    <IconButton size="small" aria-label="Clear search" onClick={() => { setTyped(''); go({ search: '', page: 0 }); }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1.25 }}>
            <Typography variant="body2" color="text.secondary" aria-live="polite">
              {shown
                ? `${count(shown.total)} ${shown.total === 1 ? 'workflow' : 'workflows'}${view === 'all' ? ' of ours' : `: ${TILES.find((t) => t.view === view)?.label.toLowerCase()}`}${search ? ` named like "${search}"` : ''}, newest first`
                : 'Loading…'}
            </Typography>
          </Box>
          {loading && shown ? <LinearProgress sx={{ height: 2 }} /> : <Box sx={{ height: 2 }} />}

          {error && (
            <Alert severity="error" sx={{ m: 2 }} action={<Button color="inherit" size="small" onClick={() => refetch()}>Try again</Button>}>
              {describeWorkflowError(error, 'Could not load the workflows.')}
            </Alert>
          )}

          {data && data.crmWorkflows.total === 0 && (
            <EmptyState
              icon={<AccountTreeOutlinedIcon />}
              title={search ? 'No workflows match' : view === 'all' ? 'No workflows yet' : 'None here'}
              text={
                search
                  ? `Nothing is named like "${search}".`
                  : view === 'all'
                    ? 'A workflow sends, tags and creates call tasks for people as they come in, with waits between. NationBuilder’s 41 are under “From NationBuilder”.'
                    : 'Pick another tile, or show everything.'
              }
              action={view === 'all' && !search ? newButton : <Button variant="outlined" onClick={() => { setTyped(''); go({ view: 'all', search: '', page: 0 }); }}>Show everything</Button>}
            />
          )}

          {(!shown || shown.items.length > 0) && (
            <TableContainer sx={{ ...TABLE_SX, opacity: loading && shown ? 0.6 : 1 }}>
              <Table size="small" aria-label="Workflows">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Trigger</TableCell>
                    <TableCell align="right">Steps</TableCell>
                    <TableCell align="right">In it now</TableCell>
                    <TableCell align="right">Ever enrolled</TableCell>
                    <TableCell>Last enrolled</TableCell>
                    <TableCell align="right">
                      <Box component="span" sx={visuallyHidden}>
                        Actions
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!shown && <LoadingRows columns={8} />}
                  {shown?.items.map((w) => {
                    const status = STATUS_META[w.status];
                    return (
                      <TableRow key={w.id} hover>
                        <TableCell sx={{ minWidth: 220, maxWidth: 420 }}>
                          <Link component={RouterLink} to={workflowPath(w.id)} underline="hover" color="inherit" sx={{ fontWeight: 600 }}>
                            {w.name}
                          </Link>
                          {w.description && (
                            <Typography variant="caption" color="text.secondary" component="p" noWrap title={w.description}>
                              {w.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Chip size="small" label={status.label} sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, color: status.tone, bgcolor: alpha(status.tone, 0.12) }} />
                          {isMigrated(w) && w.nbStatus && (
                            <Typography variant="caption" color="text.secondary" component="div">
                              NationBuilder: {w.nbStatus}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: 160 }}>{w.triggerType === 'EVENT' ? 'An app event' : describeTrigger(w as any)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{w.stepCount}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: w.enrollments.active ? 600 : 400 }}>{count(w.enrollments.active)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{count(w.enrollments.total)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{w.lastEnrolledAt ? dayjs(w.lastEnrolledAt).format('D MMM YYYY') : '—'}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Copy into a new draft">
                            <IconButton size="small" aria-label={`Copy ${w.name}`} onClick={() => copy(w)}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {!isMigrated(w) && w.status === 'DRAFT' && w.enrollments.total === 0 && (
                            <Tooltip title="Delete">
                              <IconButton size="small" aria-label={`Delete ${w.name}`} onClick={() => setDeleting(w)} sx={{ '&:hover': { color: 'error.main' } }}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {shown && shown.total > PAGE && (
            <>
              <Divider />
              <TablePagination component="div" count={shown.total} page={page} onPageChange={(_, p) => go({ page: p })} rowsPerPage={PAGE} rowsPerPageOptions={[PAGE]} />
            </>
          )}
        </Box>
      </Box>

      {deleting && (
        <DeleteDialog
          title={`Delete ${deleting.name}?`}
          confirm="Delete"
          running={removal.loading}
          error={removal.error ? describeWorkflowError(removal.error) : undefined}
          onConfirm={confirmDelete}
          onClose={() => setDeleting(null)}
        >
          <span>A draft nobody has been in: nothing else is lost. Its email campaigns stay as drafts.</span>
        </DeleteDialog>
      )}
    </CrmPageFrame>
  );
};
